import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  id: string;
  username: string;
  displayName: string;
  avatar: string;
  discriminator: string;
}

interface Guild {
  id: string;
  name: string;
  icon: string | null;
  owner: boolean;
  permissions: string;
  hasBot?: boolean;
}

interface BotOnlyGuild {
  id: string;
  name: string;
  icon: string | null;
}

interface AuthState {
  user: User | null;
  guilds: Guild[];
  botOnlyGuilds: BotOnlyGuild[];
  token: string | null;
  loading: boolean;
  error: string | null;
}

interface AuthContextType extends AuthState {
  login: () => void;
  logout: () => Promise<void>;
  retry: () => void;
  refreshGuilds: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

const API_BASE = import.meta.env.VITE_API_URL || '';
const DEV_MODE = import.meta.env.DEV;

// ── Logging helpers ────────────────────────────────────────────
const LOG_PREFIX = '%c[Auth]';
const LOG_STYLE = 'color: #5865F2; font-weight: bold';
const ERR_STYLE = 'color: #ED4245; font-weight: bold';
const OK_STYLE = 'color: #57F287; font-weight: bold';

function logInfo(...args: any[]) { console.log(LOG_PREFIX, LOG_STYLE, ...args); }
function logOk(...args: any[]) { console.log(LOG_PREFIX, OK_STYLE, ...args); }
function logWarn(...args: any[]) { console.warn(LOG_PREFIX, ERR_STYLE, ...args); }
function logError(...args: any[]) { console.error(LOG_PREFIX, ERR_STYLE, ...args); }

function truncateToken(token: string | null): string {
  if (!token) return '(none)';
  if (token === 'dev-token') return 'dev-token';
  return `${token.slice(0, 12)}…${token.slice(-6)}`;
}

// Mock data for dev mode — lets you browse the dashboard without OAuth
const DEV_USER: User = {
  id: '1124389055598170182',
  username: 'dev',
  displayName: 'Dev User',
  avatar: 'https://cdn.discordapp.com/avatars/1124389055598170182/a_placeholder.png',
  discriminator: '0',
};

const DEV_GUILD: Guild = {
  id: '1278580577104040018',
  name: 'Dev Server',
  icon: null,
  owner: true,
  permissions: '2147483647',
  hasBot: true,
};

const DEV_BOT_ONLY_GUILD: BotOnlyGuild = {
  id: '9999999999999999999',
  name: 'A Server You\'re In (Bot Present)',
  icon: null,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  // Resolve token synchronously on first render — no race condition
  const resolvedToken = (() => {
    // Check query params first (legacy)
    const params = new URLSearchParams(window.location.search);
    let urlToken = params.get('token');

    // Check hash fragment (new approach — avoids URI length limits)
    if (!urlToken && window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      urlToken = hashParams.get('token');
    }

    if (urlToken) {
      logInfo('Token found in URL:', truncateToken(urlToken));
      localStorage.setItem('anya_session_token', urlToken);
      // Clean URL (remove both query params and hash) — deferred to avoid blocking render
      queueMicrotask(() => {
        window.history.replaceState({}, '', window.location.pathname);
        logInfo('URL cleaned → redirected to', window.location.pathname);
      });
      return urlToken;
    }

    const saved = localStorage.getItem('anya_session_token');
    const isMock = params.get('mock') === 'true' || localStorage.getItem('anya_mock_mode') === 'true';

    // If we're not in mock mode but have the dev-token saved, clear it!
    if (saved === 'dev-token' && !isMock) {
      logInfo('Clearing dev-token from localStorage since mock mode is not active');
      localStorage.removeItem('anya_session_token');
      return null;
    }

    if (saved) {
      logInfo('Token found in localStorage:', truncateToken(saved));
    } else {
      logInfo('No token found in URL or localStorage');
    }
    return saved;
  })();

  const [state, setState] = useState<AuthState>({
    user: null,
    guilds: [],
    botOnlyGuilds: [],
    token: resolvedToken,
    loading: true,
    error: null,
  });

  // Track retry requests
  const [retryCount, setRetryCount] = useState(0);
  const [forceRefresh, setForceRefresh] = useState(false);

  // Fetch user data when token changes (or on retry)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isMock = params.get('mock') === 'true' || localStorage.getItem('anya_mock_mode') === 'true';

    if (isMock) {
      localStorage.setItem('anya_mock_mode', 'true');
    } else {
      localStorage.removeItem('anya_mock_mode');
    }

    if (!state.token) {
      if (isMock && DEV_MODE) {
              logInfo('DEV MODE & MOCK ACTIVE: Using mock user data');
        setState({
          user: DEV_USER,
          guilds: [DEV_GUILD],
          botOnlyGuilds: [DEV_BOT_ONLY_GUILD],
          token: 'dev-token',
          loading: false,
          error: null,
        });
        return;
      }
      logInfo('No token available — showing login screen');
      setState((s) => ({ ...s, loading: false, user: null, guilds: [] }));
      return;
    }

    // Skip API call for dev token
        if (state.token === 'dev-token') {
      if (isMock) {
        setState({
          user: DEV_USER,
          guilds: [DEV_GUILD],
          botOnlyGuilds: [DEV_BOT_ONLY_GUILD],
          token: 'dev-token',
          loading: false,
          error: null,
        });
      } else {
        localStorage.removeItem('anya_session_token');
        setState({ user: null, guilds: [], botOnlyGuilds: [], token: null, loading: false, error: null });
      }
      return;
    }

    let cancelled = false;

    async function fetchMe() {
      const apiUrl = `${API_BASE}/api/auth/me${forceRefresh ? '?refresh=true' : ''}`;
      logInfo(`Fetching user data from ${apiUrl}...`);
      const startTime = performance.now();
      // Reset the force-refresh flag once we've consumed it
      if (forceRefresh) setForceRefresh(false);

      try {
        const res = await fetch(apiUrl, {
          headers: { Authorization: `Bearer ${state.token}` },
        });

        const elapsed = Math.round(performance.now() - startTime);

        if (!res.ok) {
          const errorBody = await res.text().catch(() => '(failed to read body)');
          logError(`/api/auth/me failed — HTTP ${res.status} (${elapsed}ms)`, errorBody);

          if (!cancelled) {
            if (res.status === 401) {
              // Token is explicitly invalid/expired — clear it
              logWarn('Token is invalid/expired (401) — clearing localStorage');
              localStorage.removeItem('anya_session_token');

              if (isMock && DEV_MODE) {
                logInfo('DEV MODE & MOCK ACTIVE: Falling back to mock data');
                setState({
                  user: DEV_USER,
                  guilds: [DEV_GUILD],
                  botOnlyGuilds: [DEV_BOT_ONLY_GUILD],
                  token: 'dev-token',
                  loading: false,
                  error: null,
                });
              } else {
                setState({
                  user: null,
                  guilds: [],
                  botOnlyGuilds: [],
                  token: null,
                  loading: false,
                  error: 'Session expired — please log in again.',
                });
              }
            } else {
              // Non-401 error (500, network issue, CORS) — DON'T clear the token, let user retry
              logWarn(`Server error (${res.status}) — keeping token for retry`);
              setState((s) => ({
                ...s,
                loading: false,
                error: `Server error (${res.status}). Please try again.`,
              }));
            }
          }
          return;
        }

        const data = await res.json();
        logOk(`User loaded in ${elapsed}ms: ${data.user?.displayName || data.user?.username} (${data.user?.id})`);
        logInfo(`Guilds: ${data.guilds?.length || 0} admin servers`);
        if (data.guilds?.length) {
          logInfo('Servers:', data.guilds.map((g: any) => g.name).join(', '));
        }

                if (!cancelled) {
          setState({
            user: data.user,
            guilds: data.guilds,
            botOnlyGuilds: data.botOnlyGuilds || [],
            token: state.token,
            loading: false,
            error: null,
          });
        }
      } catch (err) {
        const elapsed = Math.round(performance.now() - startTime);
        logError(`Network error after ${elapsed}ms:`, err);

        if (!cancelled) {
          // Network error — DON'T clear the token, it might be a transient failure
          if (isMock && DEV_MODE) {
                      logInfo('DEV MODE & MOCK ACTIVE: Network failed, falling back to mock data');
            setState({
              user: DEV_USER,
              guilds: [DEV_GUILD],
              botOnlyGuilds: [DEV_BOT_ONLY_GUILD],
              token: 'dev-token',
              loading: false,
              error: null,
            });
          } else {
            setState((s) => ({
              ...s,
              loading: false,
              error: 'Could not connect to the server. Check your connection and try again.',
            }));
          }
        }
      }
    }

    fetchMe();
    return () => { cancelled = true; };
  }, [state.token, retryCount, forceRefresh]);

  const login = () => {
    const loginUrl = `${API_BASE}/api/auth/login`;
    logInfo('Redirecting to Discord OAuth →', loginUrl);
    window.location.href = loginUrl;
  };

  const logout = async () => {
    logInfo('Logging out...');
    if (state.token) {
      try {
        await fetch(`${API_BASE}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${state.token}` },
        });
        logOk('Server session destroyed');
      } catch (e) {
        logWarn('Failed to notify server of logout:', e);
      }
    }
        localStorage.removeItem('anya_session_token');
    logInfo('localStorage cleared');
    setState({ user: null, guilds: [], botOnlyGuilds: [], token: null, loading: false, error: null });
  };

  const retry = () => {
    logInfo('Retrying auth...');
    setState((s) => ({ ...s, loading: true, error: null }));
    setRetryCount((c) => c + 1);
  };

  // Refresh guild list only (bypasses server-side cache)
  const refreshGuilds = () => {
    logInfo('Refreshing guild list (force)...');
    setState((s) => ({ ...s, loading: true }));
    setForceRefresh(true);
    setRetryCount((c) => c + 1);
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        retry,
        refreshGuilds,
        isAuthenticated: !!state.user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
